import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { MyButton } from '../../index';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import { tablePaginationStyles } from './TablePagination.styles';

const TablePagination = ({
  totalRows = 0,
  currentPage = 1,
  rowsPerPage = 20,
  onPageChange,
  maxPageButtons = 5,
  // Page-size selector — opt-in so we don't change the look of existing
  // pages that still use client-side pagination.
  showPageSizeSelector = false,
  pageSizeOptions = [20, 50, 100],
  onPageSizeChange,
}) => {
  const { t } = useTranslation();
  const totalPages = Math.ceil(totalRows / rowsPerPage);
  const startRow = (currentPage - 1) * rowsPerPage + 1;
  const endRow = Math.min(currentPage * rowsPerPage, totalRows);

  const handlePageChange = page => {
    if (page >= 1 && page <= totalPages && onPageChange) {
      onPageChange(page);
    }
  };

  const getVisiblePages = () => {
    const pages = [];
    const half = Math.floor(maxPageButtons / 2);

    let start = Math.max(1, currentPage - half);
    let end = Math.min(totalPages, start + maxPageButtons - 1);

    if (end - start + 1 < maxPageButtons) {
      start = Math.max(1, end - maxPageButtons + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages;
  };

  // Reusable row-count + page-size selector block, rendered on the left.
  const renderInfoBlock = (infoText) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      {showPageSizeSelector && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={tablePaginationStyles.rowsInfo}>
            {t('table.pagination.rowsPerPage')}
          </Typography>
          <FormControl size="small" sx={{ minWidth: 72 }}>
            <Select
              value={rowsPerPage}
              onChange={(e) => {
                if (onPageSizeChange) onPageSizeChange(Number(e.target.value));
              }}
              sx={{
                height: 32,
                fontSize: '0.875rem',
                '& .MuiSelect-select': {
                  py: 0.5,
                },
              }}
            >
              {pageSizeOptions.map((opt) => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}
      <Typography sx={tablePaginationStyles.rowsInfo}>{infoText}</Typography>
    </Box>
  );

  if (totalPages <= 1) {
    return (
      <Box sx={tablePaginationStyles.container}>
        {renderInfoBlock(`${totalRows} ${t('table.pagination.rows')}`)}
        <Box />
      </Box>
    );
  }

  return (
    <Box sx={tablePaginationStyles.container}>
      {renderInfoBlock(
        `${startRow}-${endRow} ${t('table.pagination.of')} ${totalRows} ${t('table.pagination.rows')}`
      )}

      <Box sx={tablePaginationStyles.pagination}>
        <IconButton
          onClick={() => handlePageChange(1)}
          disabled={currentPage === 1}
          sx={tablePaginationStyles.navButton}
          size="small"
        >
          <FirstPageIcon />
        </IconButton>
        <IconButton
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          sx={tablePaginationStyles.navButton}
          size="small"
        >
          <ChevronLeftIcon />
        </IconButton>
        {getVisiblePages().map(page => (
          <MyButton
            key={page}
            onClick={() => handlePageChange(page)}
            customVariant={
              page === currentPage ? 'paginationActive' : 'pagination'
            }
            size="small"
          >
            {page}
          </MyButton>
        ))}
        <IconButton
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          sx={tablePaginationStyles.navButton}
          size="small"
        >
          <ChevronRightIcon />
        </IconButton>
        <IconButton
          onClick={() => handlePageChange(totalPages)}
          disabled={currentPage === totalPages}
          sx={tablePaginationStyles.navButton}
          size="small"
        >
          <LastPageIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

export default TablePagination;
